import { AttributesValueType, ProductCategoryType } from '@iota/iota-management-portal-v4-client';
import { ProductCategoryLabel } from './product-category';

export type IotaConfigAttributeScope = 'PRODUCT' | 'SUB_PRODUCT' | 'COMPANY' | 'COMPANY_REGISTRATION_PERIOD';
// B2C_SETTINGS and the PRODUCT_* keys group config attributes only; they are not backend product categories.
export type IotaConfigAttributeCategory =
    | 'GENERAL'
    | 'SHOWCASE'
    | 'LIFE_CYCLE'
    | 'B2C_SETTINGS'
    | 'PRODUCT_TERMS'
    | 'PRODUCT_SINGLE_SELECT_SETTINGS'
    | 'PRODUCT_CLASSIC_SELECT_SETTINGS'
    | ProductCategoryType;
export type IotaConfigAttributeUnit = 'EURO' | 'PERCENT' | 'MONTHS';

export const IotaConfigAttributeCategoryLabel: { [key in IotaConfigAttributeCategory]: string } = {
    GENERAL: $localize`General`,
    SHOWCASE: $localize`Showcase`,
    LIFE_CYCLE: $localize`Product Lifecycle`,
    B2C_SETTINGS: $localize`B2C Settings`,
    PRODUCT_TERMS: $localize`Product Terms`,
    PRODUCT_SINGLE_SELECT_SETTINGS: $localize`Product Single Select Settings`,
    PRODUCT_CLASSIC_SELECT_SETTINGS: $localize`Product Classic Select Settings`,
    ...ProductCategoryLabel,
    // Section header only; the product category itself keeps its shorter label elsewhere.
    BIKE_LEASING: $localize`Bike Leasing Settings`
};

// Overrides that must not leak into the other scopes.
export const SCOPED_ATTRIBUTE_CATEGORY_LABEL_OVERRIDES: Partial<
    Record<IotaConfigAttributeScope, Partial<Record<IotaConfigAttributeCategory, string>>>
> = {
    PRODUCT: {
        GENERAL: $localize`Product General`,
        [ProductCategoryType.URBAN_SPORTS_CLUB]: $localize`Urban Sports Club Settings`,
        [ProductCategoryType.MOCK_BASKET_PRODUCT]: $localize`Mock Basket Product`
    },
    SUB_PRODUCT: {
        GENERAL: $localize`Sub-Products & Services Attributes`
    },
    COMPANY_REGISTRATION_PERIOD: {
        [ProductCategoryType.PREVENTION_COURSES]: $localize`GKV Explorer & Prevention courses`
    }
};

export const ATTRIBUTE_CATEGORY_ORDER: IotaConfigAttributeCategory[] = [
    'GENERAL',
    'PRODUCT_TERMS',
    'LIFE_CYCLE',
    'PRODUCT_SINGLE_SELECT_SETTINGS',
    'PRODUCT_CLASSIC_SELECT_SETTINGS',
    ProductCategoryType.BIKE_LEASING,
    ProductCategoryType.URBAN_SPORTS_CLUB,
    ProductCategoryType.MOCK_BASKET_PRODUCT,
    ProductCategoryType.SDK_GESUNDWERKER_INPATIENT_CARE
];

export const PRODUCT_CATEGORY_BOUND_ATTRIBUTE_CATEGORIES: Partial<Record<IotaConfigAttributeCategory, ProductCategoryType[]>> = {
    [ProductCategoryType.BIKE_LEASING]: [ProductCategoryType.BIKE_LEASING],
    [ProductCategoryType.SDK_GESUNDWERKER_INPATIENT_CARE]: [ProductCategoryType.SDK_GESUNDWERKER_INPATIENT_CARE],
    [ProductCategoryType.URBAN_SPORTS_CLUB]: [
        ProductCategoryType.URBAN_SPORTS_CLUB,
        ProductCategoryType.URBAN_SPORTS_CLUB_PRIVATE_MEMBERSHIP
    ],
    [ProductCategoryType.MOCK_BASKET_PRODUCT]: [ProductCategoryType.MOCK_BASKET_PRODUCT]
};

export const IotaConfigAttributeUnitLabel: { [key in IotaConfigAttributeUnit]: string } = {
    EURO: 'EUR',
    MONTHS: $localize`month(s)`,
    PERCENT: '%'
};

export interface IotaConfigAttribute {
    // The actual name in the system
    name?: string;
    // A short description/name shown to the user
    description?: Record<string, string>;
    // An optional tooltip for additional information
    tooltip?: Record<string, string>;
    // An optional unit like "EUR" or "months"
    unit?: IotaConfigAttributeUnit;
    // Object, on which the attribute appears
    scopes?: IotaConfigAttributeScope[];
    type?: AttributesValueType;
    category?: IotaConfigAttributeCategory;
    required?: boolean;
    deprecated?: boolean;
    isArray?: boolean;
    // If only a few values are possible, we store them here
    possibleValues?: string[];
    // Values can have a map for each value which includes a map with labels for each language
    possibleValuesLabels?: Record<string, Record<string, string>>;

    // Custom unit which can be set in the code (e.g. if it depends on other attributes)
    customUnit?: string;
    cannotBeOverwritten?: boolean;
}

export interface IotaConfig {
    locales?: string[];
    product_categories?: Record<string, string[]>[];
    attributes?: IotaConfigAttribute[];
}
